import { useEffect, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Textarea } from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";

export interface MergeUsersDialogProps {
  open: boolean;
  onClose: () => void;
  defaultSurvivorId?: string;
  onSubmit: (input: {
    survivorUserId: string;
    duplicateUserId: string;
    reason: string;
    approvalId: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function MergeUsersDialog({
  open,
  onClose,
  defaultSurvivorId = "",
  onSubmit,
  busy = false,
  error = null,
}: MergeUsersDialogProps) {
  const [survivorUserId, setSurvivorUserId] = useState(defaultSurvivorId);
  const [duplicateUserId, setDuplicateUserId] = useState("");
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");

  useEffect(() => {
    if (open) {
      setSurvivorUserId(defaultSurvivorId);
    }
  }, [open, defaultSurvivorId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Merge Users"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={
              survivorUserId.trim().length === 0 ||
              duplicateUserId.trim().length === 0 ||
              reason.trim().length < 3 ||
              approvalId.trim().length === 0
            }
            onClick={() =>
              void onSubmit({
                survivorUserId: survivorUserId.trim(),
                duplicateUserId: duplicateUserId.trim(),
                reason: reason.trim(),
                approvalId: approvalId.trim(),
              })
            }
          >
            Merge
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Merge requires Super Admin approval. The duplicate User is closed;
          lineage is preserved on the survivor. This UI does not bypass backend
          authority checks.
        </Text>
        <Input
          label="Survivor User ID"
          value={survivorUserId}
          onChange={(event) => setSurvivorUserId(event.target.value)}
        />
        <Input
          label="Duplicate User ID"
          value={duplicateUserId}
          onChange={(event) => setDuplicateUserId(event.target.value)}
        />
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <ApprovalPickerField
          label="Approved Approval (required)"
          value={approvalId}
          onChange={setApprovalId}
          required
          minAuthority="dual"
        />
        {error ? <Alert tone="danger" title="Merge failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}
